/* MapleWeld - province-aware shipping line (assets/mwh-ship-geo.js)
   2026-10-06 (v2: fixed self-nesting loop).
   2026-10-16 (v3: free-shipping exclusion note with * on every free offer).

   WHAT IT DOES
   Shows each visitor the flat rate and free-shipping amount for THEIR province
   on the product page (the shipping line under the buy buttons) and on the
   cart page (the yellow box). The province comes from, in order:
     1. the visitor's own choice in the dropdown (remembered 30 days), then
     2. an approximate guess from their IP address, via a public lookup
        (ipwho.is, then ipapi.co as a fallback). Cached 7 days in the browser.
   IP location is a guess (mobile networks and VPNs are often wrong), so a
   "change" dropdown is always shown. If both lookups fail, the page keeps its
   plain fallback wording and the dropdown asks the visitor to choose.

   FREE-SHIPPING EXCLUSION (v3): wherever free shipping is offered, "FREE" gets
   a red * and a short note underneath says overweight, oversized and
   dangerous-goods items are not included and are charged extra. Same rule as
   the Shipping Policy page. Edit the wording in NOTE below.

   KEEP IN SYNC: the figures in RATES below are plain text. They are NOT read
   from Settings > Shipping and delivery. If a rate changes there, change it
   here, on the Shipping Policy page and in the ticker, or the page will
   promise a rate checkout does not charge.

   PRIVACY: the lookup sends the visitor's IP address to the lookup service.
   Mention it in the privacy policy. Set LOOKUP = false to switch it off; the
   dropdown still works on its own.

   v2 FIX: v1 matched its own output (it contains the word "shipping"), so the
   observer kept painting the widget inside itself. Now a list item that
   already holds a widget is skipped, and nothing inside a widget is a target.

   TARGETS (all optional, nothing breaks if absent):
     .mw-cart-ship          cart page box (whole box content is replaced)
     [data-mw-ship]         any element you add yourself (content replaced)
     .mwp__ful li           product page: the list item that mentions shipping
*/
(function () {
  'use strict';

  var LOOKUP = true;
  var KEY_CHOICE = 'mwShipProvChoice';
  var KEY_GUESS = 'mwShipProvGuess';
  var DAY = 86400000;

  /* Shown under every free-shipping offer, marked with the red *. */
  var NOTE = 'Overweight, oversized and dangerous-goods items are not included in free shipping. Extra shipping charges apply to these items.';

  /* flat = flat rate under the free amount, free = free-shipping amount. */
  var RATES = {
    ON: { n: 'Ontario', flat: 15, free: 100 },
    QC: { n: 'Quebec', flat: 15, free: 150 },
    SK: { n: 'Saskatchewan', flat: 20, free: 200 },
    AB: { n: 'Alberta', flat: 30, free: 250 },
    BC: { n: 'British Columbia', flat: 30, free: 250 },
    MB: { n: 'Manitoba', flat: 30, free: 250 },
    NB: { n: 'New Brunswick', flat: 30, free: 250 },
    NL: { n: 'Newfoundland & Labrador', flat: 30, free: 250 },
    NS: { n: 'Nova Scotia', flat: 30, free: 250 },
    PE: { n: 'Prince Edward Island', flat: 30, free: 250 },
    YT: { n: 'Yukon', live: true },
    NT: { n: 'Northwest Territories', live: true },
    NU: { n: 'Nunavut', live: true }
  };
  var ORDER = ['ON', 'QC', 'SK', 'AB', 'BC', 'MB', 'NB', 'NL', 'NS', 'PE', 'YT', 'NT', 'NU'];

  var current = '';
  var painting = false;

  function store(kind) {
    try { return window[kind]; } catch (e) { return null; }
  }
  function read(key, maxAge) {
    try {
      var ls = store('localStorage');
      if (!ls) return '';
      var raw = ls.getItem(key);
      if (!raw) return '';
      var o = JSON.parse(raw);
      if (!o || !o.v || !RATES[o.v]) return '';
      if (Date.now() - o.t > maxAge) return '';
      return o.v;
    } catch (e) { return ''; }
  }
  function write(key, val) {
    try {
      var ls = store('localStorage');
      if (ls) ls.setItem(key, JSON.stringify({ v: val, t: Date.now() }));
    } catch (e) { /* storage blocked: fine */ }
  }

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  function css() {
    if (document.getElementById('mwsg-css')) return;
    var st = document.createElement('style');
    st.id = 'mwsg-css';
    st.textContent =
      '.mwsg{display:block;line-height:1.5}' +
      '.mwsg__row{display:flex;flex-wrap:wrap;align-items:center;gap:6px 8px}' +
      '.mwsg__lbl{font-weight:600}' +
      '.mwsg select{font:inherit;font-size:.95em;padding:3px 24px 3px 8px;border:1px solid #cfd3d7;border-radius:6px;background:#fff;color:#16181B;max-width:100%;cursor:pointer}' +
      '.mwsg__free{display:inline-block;background:#FFD400;color:#16181B;font-weight:800;font-size:.85em;letter-spacing:.03em;padding:1px 8px;border-radius:4px}' +
      '.mwsg__ast{color:#C8102E;font-weight:800;margin-left:1px}' +
      '.mwsg__line{display:block;margin-top:4px}' +
      '.mwsg__line a{text-decoration:underline;font-weight:600;color:inherit}' +
      '.mwsg__note{display:flex;gap:5px;margin-top:6px;padding:6px 9px;border-radius:6px;background:#FFF5F6;border-left:3px solid #C8102E;font-size:.86em;line-height:1.45;color:#3A4451}' +
      '.mwsg__note b{color:#C8102E;font-weight:800}';
    document.head.appendChild(st);
  }

  var AST = '<span class="mwsg__ast" aria-hidden="true">*</span>';

  function hasFree(code) {
    var r = RATES[code];
    return !r || !r.live;
  }

  function lineHtml(code) {
    var r = RATES[code];
    if (!r) {
      return 'Free shipping' + AST + ' from <b>$100</b> to <b>$250</b>, depending on your province.';
    }
    if (r.live) {
      return 'Shipping to <b>' + esc(r.n) + '</b> is calculated at checkout from live carrier rates. Free shipping is not available here.';
    }
    return '<b>$' + r.flat + '</b> flat rate &middot; <span class="mwsg__free">FREE</span>' + AST + ' on orders <b>$' + r.free + '+</b> (before taxes)';
  }

  function noteHtml(code) {
    if (!hasFree(code)) return '';
    return '<span class="mwsg__note"><b aria-hidden="true">*</b><span>' + esc(NOTE) + '</span></span>';
  }

  function selectHtml(code) {
    var h = '<select aria-label="Choose your province for shipping rates" data-mwsg-sel>';
    if (!code) h += '<option value="" selected>Choose your province</option>';
    for (var i = 0; i < ORDER.length; i++) {
      var c = ORDER[i];
      h += '<option value="' + c + '"' + (c === code ? ' selected' : '') + '>' + esc(RATES[c].n) + '</option>';
    }
    return h + '</select>';
  }

  function html() {
    return '<span class="mwsg">' +
      '<span class="mwsg__row"><span class="mwsg__lbl">Shipping to</span>' + selectHtml(current) + '</span>' +
      '<span class="mwsg__line">' + lineHtml(current) +
      ' <a href="/pages/shipping-policy">All rates</a></span>' +
      noteHtml(current) + '</span>';
  }

  function inWidget(el) {
    return !!(el.closest && (el.closest('.mwsg') || (el.parentNode && el.parentNode.closest && el.parentNode.closest('[data-mwsg]'))));
  }

  function targets() {
    var out = [];
    var i, n;
    n = document.querySelectorAll('.mw-cart-ship,[data-mw-ship]');
    for (i = 0; i < n.length; i++) {
      if (!inWidget(n[i])) out.push(n[i]);
    }
    n = document.querySelectorAll('.mwp__ful li');
    for (i = 0; i < n.length; i++) {
      var li = n[i];
      /* already holds a widget: leave it alone */
      if (li.querySelector('[data-mwsg]') || li.querySelector('.mwsg')) continue;
      if (/shipping/i.test(li.textContent || '')) {
        var spans = li.querySelectorAll('span');
        if (spans.length) out.push(spans[spans.length - 1]);
      }
    }
    return out;
  }

  function paint(el) {
    painting = true;
    el.setAttribute('data-mwsg', '1');
    el.innerHTML = html();
    var sel = el.querySelector('select[data-mwsg-sel]');
    if (sel) {
      sel.addEventListener('change', function () {
        if (sel.value && RATES[sel.value]) {
          current = sel.value;
          write(KEY_CHOICE, current);
          refresh();
        }
      });
    }
    painting = false;
  }

  function enhance() {
    var t = targets();
    for (var i = 0; i < t.length; i++) {
      if (!t[i].getAttribute('data-mwsg')) paint(t[i]);
    }
  }

  function refresh() {
    var n = document.querySelectorAll('[data-mwsg]');
    for (var i = 0; i < n.length; i++) {
      /* repaint top-level widgets only */
      if (n[i].parentNode && n[i].parentNode.closest && n[i].parentNode.closest('[data-mwsg]')) continue;
      paint(n[i]);
    }
  }

  function fromGeo(data) {
    if (!data) return '';
    var cc = (data.country_code || data.countryCode || '').toUpperCase();
    var rc = (data.region_code || data.regionCode || '').toUpperCase();
    if (cc === 'CA' && RATES[rc]) return rc;
    return '';
  }

  function getJson(url, cb) {
    var done = false;
    var timer = setTimeout(function () { if (!done) { done = true; cb(null); } }, 4000);
    try {
      fetch(url, { credentials: 'omit', referrerPolicy: 'no-referrer' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (j) { if (!done) { done = true; clearTimeout(timer); cb(j); } })
        .catch(function () { if (!done) { done = true; clearTimeout(timer); cb(null); } });
    } catch (e) { if (!done) { done = true; clearTimeout(timer); cb(null); } }
  }

  function guess(cb) {
    getJson('https://ipwho.is/?fields=success,country_code,region_code', function (a) {
      var c = fromGeo(a);
      if (c) return cb(c);
      getJson('https://ipapi.co/json/', function (b) { cb(fromGeo(b)); });
    });
  }

  function init() {
    css();
    current = read(KEY_CHOICE, 30 * DAY) || read(KEY_GUESS, 7 * DAY) || '';
    enhance();
    if (!window.fetch) return;
    if (!current && LOOKUP) {
      guess(function (c) {
        if (c && !read(KEY_CHOICE, 30 * DAY)) {
          current = c;
          write(KEY_GUESS, c);
          refresh();
        }
      });
    }
    /* The cart page can re-render its panels over AJAX; paint anything new. */
    if (window.MutationObserver) {
      var timer = null;
      new MutationObserver(function () {
        if (painting) return;
        clearTimeout(timer);
        timer = setTimeout(enhance, 200);
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
